import { Component, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatListOption, MatSelectionList, MatSelectionListChange } from '@angular/material/list';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { ColumnHeaderFilterType, IFilterEvent } from './models/abstractions/filter.interface';

@Component({
    selector: 'app-table-filter',
    template: ` <form novalidate autocomplete="off" [formGroup]="formGroup" style="color:#545b64">
      @if (type !== 'date' && type !== 'list') {
        <mat-form-field appearance="fill" class="full-width" color="accent">
          <input matInput placeholder="Filter..." formControlName="value" autocomplete="off" type="{{ type }}" />
          @if (value) {
            <mat-icon matSuffix style="cursor:pointer" (click)="onReset(type)">close</mat-icon>
          }
        </mat-form-field>
      }
      @if (type === 'date') {
        <mat-form-field appearance="fill" class="full-width" color="accent">
          <input
            matInput
            [matDatepicker]="picker"
            readonly
            placeholder="Filter..."
            (click)="picker.open()"
            (dateChange)="onChangePicker($event)"
            style="cursor:pointer"
            formControlName="value"
            autocomplete="off" />
          <mat-datepicker #picker></mat-datepicker>
          @if (value) {
            <mat-icon matSuffix style="cursor:pointer" (click)="onReset(type)">close</mat-icon>
          }
        </mat-form-field>
      }
      @if (type === 'list') {
        <mat-form-field
          color="accent"
          appearance="fill"
          class="full-width"
          [matMenuTriggerFor]="menu"
          #menuTrigger="matMenuTrigger"
          disabled>
          <input
            matInput
            placeholder="Filter..."
            formControlName="value"
            style="cursor:pointer"
            autocomplete="off"
            type="text"
            readonly />
          @if (value) {
            <mat-icon matSuffix style="cursor:pointer" (click)="onResetList($event)">close</mat-icon>
          }
        </mat-form-field>
      }
      <mat-menu #menu="matMenu" [overlapTrigger]="false" yPosition="below">
        <ng-template matMenuContent>
          <mat-selection-list #items (selectionChange)="onSelectionChanged($event, items.selectedOptions)">
            @for (item of values; track item) {
              <mat-list-option [selected]="value?.includes(item)">
                {{ item }}
              </mat-list-option>
            }
          </mat-selection-list>
        </ng-template>
      </mat-menu>
    </form>`,
    standalone: false
})
export class FilterComponent {
  @ViewChild(MatSelectionList, { static: true })
  private _list: MatSelectionList;

  private _key: string;

  @Input()
  set key(value: string) {
    this._key = value;
  }

  private _type: ColumnHeaderFilterType;

  @Input()
  set type(value: ColumnHeaderFilterType) {
    this._type = value;
  }

  get type(): ColumnHeaderFilterType {
    return this._type;
  }

  @Input()
  set value(value: any) {
    if (value) {
      this.formGroup.patchValue({
        value: value,
      });
    }
  }

  get value(): any {
    return this.formGroup.controls.value.value;
  }

  private _values: any[];

  @Input()
  set values(value: any[]) {
    this._values = value;
  }

  get values(): any[] {
    return this._values;
  }

  private _filterChanged: EventEmitter<IFilterEvent>;

  @Output()
  get filterChanged(): EventEmitter<IFilterEvent> {
    return this._filterChanged;
  }

  private _inputValueChange = new Subject<any>();

  constructor() {

    this._type = ColumnHeaderFilterType.Text;
    this._filterChanged = new EventEmitter();

    this._inputValueChange
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        map(value => {
          this.filterChanged.emit({
            key: this._key,
            value: value,
            type: this._type
          });
        })
      )
      .subscribe();

    this._formGroup = new FormGroup({
      value: new FormControl(null),
    });
  }

  private _formGroup: FormGroup;

  get formGroup(): FormGroup {
    return this._formGroup;
  }

  @HostListener('input', ['$event.target.value'])
  onChangeFilter(value: string): void {

    switch (this._type) {
      case ColumnHeaderFilterType.Number: {
        const numericValue = Number(value);
        this._inputValueChange.next(value == '' || isNaN(numericValue) ? value : numericValue);
        break;
      }
      default:
        this._inputValueChange.next(value);
        break;
    }
  }

  onChangePicker(event: MatDatepickerInputEvent<Date>) {

    this.filterChanged.emit({
      key: this._key,
      value: event.value.toISOString().substring(0, 10),
      type: ColumnHeaderFilterType.Date
    });
  }

  onSelectionChanged(event: MatSelectionListChange, model: any) {
    const value = model.selected
      .map((item: MatListOption) => {
        return item.getLabel().trim();
      })
      .join(',');

    this._formGroup.patchValue({
      value: value,
    });

    this.filterChanged.emit({
      key: this._key,
      value: value,
      type: ColumnHeaderFilterType.List
    });
  }

  onReset(type: ColumnHeaderFilterType): void {

    this._formGroup.reset();

    this.filterChanged.emit({
      key: this._key,
      value: null,
      type: type
    });
  }

  onResetList(event: Event): void {
    event.stopPropagation();

    this._formGroup.reset();

    this.filterChanged.emit({
      key: this._key,
      value: null,
      type: ColumnHeaderFilterType.List
    });
  }
}
